/**
 * DMS input field
 * It is a fork of NumberInput. It allows text input in a format D/M/S (autoformat)
 * 
 * NumberInput doesn't allow slash character ("/"). 
 * The angle conversion tool requires an input field in the format D/M/S (degree/minute/second).
 * This new input component is similar to NumericInput in terms of props, but additionally
 * applies auto-format while typing, and validation of minutes and seconds.
*/

import React, { ChangeEvent } from 'react';

interface DMSInputProps {
	id: string;
	label: string;
	value: string | number | readonly string[] | undefined;
	onChange: (event: ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
	onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
	name?: string;
	required?: boolean;
	disabled?: boolean;
	invalid?: boolean;
	errorMessage?: string;
	tooltip?: string;
	loading?: boolean;
	placeholder?: string;
	unitLabel?: string;
}

const DMSInput: React.FC<DMSInputProps> = ({
	id,
	label,
	value,
	name,
	required,
	disabled,
	invalid,
	errorMessage,
	tooltip,
	placeholder,
	loading,
	unitLabel,
	onChange,
	onBlur,
	onFocus,
}) => {

	// sanitize input of M and S
	const sanitizeInputMS = (part: string, max: number = 59) => {
		const cleaned = part.slice(0, 2); // limit to 2 digits
		const value = parseInt(cleaned, 10);
		if (!isNaN(value)) {
			return value > max ? String(max) : cleaned;
		}
		return '';
	}

	/**
	 * Format validator
	 * -----------------------
	 * Allows D/M/S 
	 * Auto insert slashes
	 * M and S max value is 59
	*/
	function autoFormatDMS(input: string) {
		// Remove leading slashes
		if (input.trim().startsWith('/')) {
			input = input.trim().replace(/^\/+/, '');
		}

		// Remove consecutive slashes
		input = input.replace(/\/{2,}/g, '/');

		// If slashes have been entered, treat as structured input and clamp M/S
		if (input.includes('/')) {
			const parts = input.split('/');

			let [d = '', m = '', s = ''] = parts.map(part => part.replace(/[^\d]/g, ''));

			// sanitize M and S
			m = sanitizeInputMS(m);
			s = sanitizeInputMS(s);

			const formattedParts = [d];
			if (parts.length > 1) formattedParts.push(m);
			if (parts.length > 2) formattedParts.push(s);

			return formattedParts.join('/');
		}

		// No slashes — user is still typing degrees
		// don't autoformat — return cleaned input as-is
		return input.replace(/[^\d]/g, '');
	}



	/**
	 * Handle onChange to autoformat input and callback
	*/
	const handleOnChange = (e) => {
		const formattedValue = autoFormatDMS(e.target.value);
		const eventWithFormattedValue = {
			...e,
			target: {
				...e.target,
				value: formattedValue
			}
		};

		onChange(eventWithFormattedValue as ChangeEvent<HTMLInputElement>);
	}


	/**
	 * handle onKeyDown
	*/
	const handleOnKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
		const allowedKeys = [
			'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '/',
		];
	
		const isCtrlOrCmd = evt.ctrlKey || evt.metaKey;
		const isDigit = /\d/.test(evt.key);
	
		// Allow Ctrl/Cmd + key (e.g. Ctrl+V)
		if (isCtrlOrCmd) return;
	
		if (!isDigit && !allowedKeys.includes(evt.key)) {
			evt.preventDefault();
		}
	};

	/**
	 * handle onPaste
	*/
	const handleOnInput = (evt: React.FormEvent<HTMLInputElement>) => {
		const input = evt.currentTarget;
		const formatted = autoFormatDMS(input.value);
		const fakeEvent = {
			...evt,
			target: {
				...input,
				value: formatted
			}
		};
	
		onChange(fakeEvent as unknown as ChangeEvent<HTMLInputElement>);
	};



	return (
		<div className={`m-input ${invalid ? 'm-input--error' : ''}`}>
			<label
				htmlFor={id}
				className={`m-input__label ${required ? 'm-input__label--required' : ''
					} ${tooltip ? 'm-input__label--with-tooltip' : ''}`}
			>
				{label}

				{!!tooltip && (
					<span className="a-tooltip">
						<span>
							<svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M12 8.03261V7M12 16.5L12 9.27174M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#2C2929"></path>
							</svg>
						</span>
						<div className="a-tooltip__text">{tooltip}</div>
					</span>
				)}
			</label>

			{loading ? (
				<div className="a-input-skeleton">
					<div></div>
				</div>
			) : (
				<>
					<input
						type="text"
						step="any"
						className="m-input__input"
						id={id}
						name={name}
						value={value === undefined ? '' : value}
						disabled={disabled}
						placeholder={placeholder ? placeholder : '---'}
						aria-describedby={invalid ? `${id}_error` : undefined}
						onChange={handleOnChange}
						onBlur={onBlur}
						onFocus={onFocus}
						onKeyDown={handleOnKeyDown}
						onInput={handleOnInput}
					/>

					{!!unitLabel && <div className="m-input__unit">{unitLabel}</div>}
				</>
			)}

			{invalid && (
				<div id={`${id}_error`} className="m-input__error">
					{errorMessage}
				</div>
			)}
		</div>
	);
};

export default DMSInput;
